/** One set of date formatters (U-222): relative time in Amsterdam calendar days (addendum A.4.7). */
const TIME_ZONE = 'Europe/Amsterdam'
const DAY_KEY = new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
const TIME = new Intl.DateTimeFormat('nl-NL', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
const DAY_MONTH = new Intl.DateTimeFormat('nl-NL', { timeZone: TIME_ZONE, day: 'numeric', month: 'short' })
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('nl-NL', { timeZone: TIME_ZONE, day: 'numeric', month: 'short', year: 'numeric' })
const WEEKDAY_DATE = new Intl.DateTimeFormat('nl-NL', { timeZone: TIME_ZONE, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
const WEEKDAY_DAY_MONTH = new Intl.DateTimeFormat('nl-NL', { timeZone: TIME_ZONE, weekday: 'short', day: 'numeric', month: 'short' })
const LONG_DATE = new Intl.DateTimeFormat('nl-NL', { timeZone: TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

/** The Amsterdam calendar day of a moment, as YYYY-MM-DD. */
export function amsterdamDay(date: Date): string {
  return DAY_KEY.format(date)
}

function dayNumber(day: string): number {
  const [year, month, date] = day.split('-').map(Number)
  return Date.UTC(year, month - 1, date) / 86_400_000
}

/**
 * "zojuist", "12 min geleden", "2 u geleden" on the same day, "gisteren 14:02", "3 dagen geleden"
 * within a week, then "12 aug" (with the year for an earlier year). Days are Amsterdam calendar
 * days, not blocks of 24 hours: 23:00 yesterday is "gisteren" at 00:30.
 */
export function formatRelatief(iso: string, now: Date = new Date()): string {
  const at = new Date(iso)
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000)
  if (minutes < 1) return 'zojuist'
  if (minutes < 60) return `${minutes} min geleden`
  const days = dayNumber(amsterdamDay(now)) - dayNumber(amsterdamDay(at))
  if (days <= 0) return `${Math.floor(minutes / 60)} u geleden`
  if (days === 1) return `gisteren ${TIME.format(at)}`
  if (days < 7) return `${days} dagen geleden`
  return amsterdamDay(at).slice(0, 4) === amsterdamDay(now).slice(0, 4) ? DAY_MONTH.format(at) : DAY_MONTH_YEAR.format(at)
}

/** The full moment for a `title` and `aria-label`: "vrijdag 16 oktober 2026, 14:02". */
export function formatDateTimeLong(iso: string): string {
  const at = new Date(iso)
  return `${LONG_DATE.format(at)}, ${TIME.format(at)}`
}

/** Korte relatieve NL-weergave, bijv. "3 wkn geleden". */
export function relatiefNl(iso: string): string {
  const dagen = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (dagen <= 0) return 'vandaag'
  if (dagen === 1) return 'gisteren'
  if (dagen < 7) return `${dagen} dgn geleden`
  if (dagen < 31) return `${Math.floor(dagen / 7)} wkn geleden`
  if (dagen < 365) return `${Math.floor(dagen / 30)} mnd geleden`
  return `${Math.floor(dagen / 365)} jr geleden`
}

/** Non-breaking spaces, so a date never wraps in the middle ("27 jul" / "2026", U-222). */
const keepTogether = (text: string) => text.replace(/ /g, '\u00a0')

const MONTH_YEAR = new Intl.DateTimeFormat('nl-NL', { timeZone: TIME_ZONE, month: 'short', year: 'numeric' })

function parse(value: string | null | undefined): Date | null {
  if (!value) return null
  const at = new Date(value)
  return Number.isNaN(at.getTime()) ? null : at
}

/**
 * The one date notation of the platform (spec-64-u2, U-222): "13 okt 2026", always with the
 * year, in Amsterdam time, never broken over two lines. "—" without a date. Takes an ISO
 * moment or a plain `YYYY-MM-DD`.
 */
export function formatDatum(value: string | null | undefined): string {
  const at = parse(value)
  return at ? keepTogether(DAY_MONTH_YEAR.format(at)) : '—'
}

/** Weekday and date with the year: "di 27 okt 2026"; "—" without a date. */
export function formatWeekdagDatum(value: string | null | undefined): string {
  const at = parse(value)
  return at ? keepTogether(WEEKDAY_DATE.format(at)) : '—'
}

/** Day and month without the year, for tight spots such as task rows: "27 okt"; "—" without a date. */
export function formatDagMaand(value: string | null | undefined): string {
  const at = parse(value)
  return at ? keepTogether(DAY_MONTH.format(at)) : '—'
}

/** Date and time: "30 sep 2026 15:09"; "—" without a moment. */
export function formatDatumTijd(value: string | null | undefined): string {
  const at = parse(value)
  return at ? `${keepTogether(DAY_MONTH_YEAR.format(at))}\u00a0${TIME.format(at)}` : '—'
}

/** Weekday, day and month without the year: "ma 12 okt" (notification times); "—" without a date. */
export function formatDagDatum(value: string | null | undefined): string {
  const at = parse(value)
  return at ? keepTogether(WEEKDAY_DAY_MONTH.format(at).replace(/\./g, '')) : '—'
}

/** Time of day only: "15:09", in Amsterdam time; "—" without a moment. */
export function formatTijd(value: string | null | undefined): string {
  const at = parse(value)
  return at ? TIME.format(at) : '—'
}

/** Month and year in full: "oktober 2026" (a month picker); `YYYY-MM`, "—" when malformed. */
const MONTH_YEAR_LONG = new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', month: 'long', year: 'numeric' })
export function formatMaandJaarLang(month: string): string {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month)
  return match ? MONTH_YEAR_LONG.format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))) : '—'
}

/** Month and year: "mrt 2026" (a founding or rejection month); "—" without a date. */
export function formatMaandJaar(value: string | null | undefined): string {
  const at = parse(value)
  return at ? keepTogether(MONTH_YEAR.format(at)) : '—'
}

/** A number of days written out: "vandaag", "1 dag", "108 dagen" (never "108d"). */
export function formatDagen(days: number): string {
  if (days <= 0) return 'vandaag'
  return days === 1 ? '1 dag' : `${days} dagen`
}

/** Time in a phase: "108 dagen in Inflow", "1 dag in fase", "sinds vandaag in Inflow". */
export function formatInFase(days: number, phase?: string): string {
  const where = phase ?? 'fase'
  return days <= 0 ? `sinds vandaag in ${where}` : `${formatDagen(days)} in ${where}`
}

const ONE_DECIMAL = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1, minimumFractionDigits: 1 })

/** A file size in Dutch notation: "512 B", "12,5 KB", "2,8 MB". */
export function formatBestandsgrootte(bytes: number): string {
  if (bytes < 1024) return `${bytes}\u00a0B`
  const kb = bytes / 1024
  if (kb < 1024) return `${ONE_DECIMAL.format(kb)}\u00a0KB`
  return `${ONE_DECIMAL.format(kb / 1024)}\u00a0MB`
}

function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const euroFormats = new Map<number, Intl.NumberFormat>()
const percentFormats = new Map<number, Intl.NumberFormat>()

/** A percentage in Dutch notation: "37,82%" (the value is already a percentage, 37.82); "—" when empty. */
export function formatPercent(value: number | string | null | undefined, digits = 2): string {
  const amount = toNumber(value)
  if (amount === null) return '—'
  let format = percentFormats.get(digits)
  if (!format) {
    format = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    percentFormats.set(digits, format)
  }
  return `${format.format(amount)}%`
}

/** An amount in euro, Dutch notation: "€ 19.800,00"; "—" when empty. */
export function formatEuro(value: number | string | null | undefined, digits = 2): string {
  const amount = toNumber(value)
  if (amount === null) return '—'
  let format = euroFormats.get(digits)
  if (!format) {
    format = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
    euroFormats.set(digits, format)
  }
  return format.format(amount)
}

const UTC_LONG_DAY = new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', day: 'numeric', month: 'long' })
const UTC_WEEKDAY_LONG_DAY = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})
const UTC_SHORT_DAY = new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', day: 'numeric', month: 'short' })

/** A calendar date (read as UTC, no time zone shift) in the long form: "29 oktober". */
export function formatUtcDagMaandLang(date: Date): string {
  return UTC_LONG_DAY.format(date)
}

/** A calendar date (UTC) with the weekday: "donderdag 29 oktober". */
export function formatUtcWeekdagDagMaandLang(date: Date): string {
  return UTC_WEEKDAY_LONG_DAY.format(date)
}

/** A calendar date (UTC) short, without the dot after the month: "9 dec". */
export function formatUtcDagMaandKort(date: Date): string {
  return UTC_SHORT_DAY.format(date).replace('.', '')
}
